import { useEffect, useLayoutEffect, useRef, useState } from "react";

const useBreadcrumbCollapse = (itemsCount: number, resetKey: string) => {
	const listRef = useRef<HTMLOListElement>(null);
	const [state, setState] = useState({ resetKey, width: null as number | null, collapsed: 0 });
	const maxCollapsed = Math.max(itemsCount - 2, 0);

	if (state.resetKey !== resetKey) setState({ ...state, resetKey, collapsed: 0 });

	useLayoutEffect(() => {
		const list = listRef.current;
		if (!list || state.collapsed >= maxCollapsed) return;
		if (list.scrollWidth > list.clientWidth) setState({ ...state, collapsed: state.collapsed + 1 });
	}, [state, maxCollapsed]);

	useEffect(() => {
		const list = listRef.current;
		if (!list) return;

		let frame = 0;
		const observer = new ResizeObserver(([entry]) => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				const width = entry.contentRect.width;
				setState((prev) => {
					if (prev.width === width) return prev;
					if (prev.width === null) return { ...prev, width };
					return { ...prev, width, collapsed: 0 };
				});
			});
		});

		observer.observe(list);
		return () => {
			observer.disconnect();
			cancelAnimationFrame(frame);
		};
	}, []);

	return {
		listRef,
		collapsed: Math.min(state.collapsed, maxCollapsed),
		isFullyCollapsed: state.collapsed >= maxCollapsed,
	};
};

export default useBreadcrumbCollapse;
